/**
 * What happens on the way into a screen.
 *
 * Kept out of the components on purpose. Route transitions are the part of a
 * SPA that a sighted mouse user never notices being broken: the URL changes,
 * the pixels change, and focus is still on a link that no longer exists while
 * the screen reader reads the tail of the previous screen. Neither symptom
 * shows up in a component test of either screen.
 *
 * Putting it in a plain module means the behaviour is testable against a DOM
 * without rendering anything, which is why SHELL-015 and SHELL-016 have unit
 * tests rather than only an E2E that could pass for the wrong reason.
 */

import { clearAnnouncements } from '@platform/announce';

/** The id carried by every screen's `<h1>`. See Screen.tsx. */
export const SCREEN_HEADING_ID = 'screen-heading';

/**
 * Move focus to the new screen's heading (SHELL-015).
 *
 * The heading carries `tabindex="-1"` so it can receive programmatic focus
 * without becoming a tab stop. Note that `:focus-visible` will not match here,
 * by design -- this focus was not keyboard-initiated, and drawing a ring on it
 * would put a highlight around the title on every navigation.
 */
export function focusScreenHeading(): void {
  document.getElementById(SCREEN_HEADING_ID)?.focus();
}

/**
 * Called once per completed route change, never on first render.
 *
 * The first-render exclusion is not ours to enforce: preact-iso's `<Router>`
 * fires `onRouteChange` only when the path actually changes, so a deep link
 * does not steal focus from the skip link on arrival. If that ever changes,
 * this is where the guard goes.
 */
export function enterScreen(): void {
  // Order matters. Clearing first drops anything the previous screen queued
  // (SHELL-016); focusing second is what produces the new screen's own
  // announcement, via the heading the AT is about to read.
  clearAnnouncements();
  focusScreenHeading();
}
